<template>
	<v-container>
		<v-btn title="Back" small icon @click="$emit('toggle')">
			<v-icon small>fa-arrow-left</v-icon>
		</v-btn>
		<list-selection
			class="selection-group"
			icon="fa-key"
			title="Roles"
			:disabled="!canEdit"
			:right-items="roles"
			:left-items="allRoles"
			:add-item="(itemId) => changeAssociationRole(itemId, 'addUserRole')"
			:remove-item="(itemId) => changeAssociationRole(itemId, 'revokeUserRole')"
			:right-props="{ enableSearch: true }"
			:left-props="{ enableSearch: true }"
		></list-selection>

		<list-selection
			class="selection-group"
			:disabled="!canEdit"
			:right-items="associations"
			:add-item="
				(itemId) => changeAssociationRole(itemId, 'addAssociationUser')
			"
			:remove-item="
				(itemId) => changeAssociationRole(itemId, 'removeAssociationUser')
			"
		>
			<template #left="{ on, value }">
				<list-box
					:value="value"
					:subheader="Object.keys(leftAssociationsByTypes)"
					:items="Object.values(leftAssociationsByTypes)"
					enable-search
					v-on="on"
				/>
			</template>

			<template #right="{ on, value }">
				<list-box
					header="Associations"
					header-icon="fa-building"
					:value="value"
					:subheader="Object.keys(rightAssociationsByTypes)"
					:items="Object.values(rightAssociationsByTypes)"
					enable-search
					v-on="on"
				/>
			</template>
		</list-selection>
	</v-container>
</template>

<script>
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";

import ListSelection from "@/components/shared/ListSelection";
import ListBox from "@/components/shared/ListBox";

import api from "@/api/cloud";

export default {
	components: {
		ListSelection,
		ListBox,
	},

	data() {
		return {
			associationSearch: "",
			rightSystems: {},
		};
	},

	computed: {
		...mapGetters("userEdit", ["canEdit", "roles", "associations"]),

		userId() {
			return this.$route.params.userId;
		},

		allRoles() {
			const roles = this.$store.getters["roles/roles"];
			return Object.values(roles);
		},

		communities() {
			let communities = this.$store.getters["communities/items"];
			communities = _.cloneDeep(communities);
			return this.filterRightAssociations(communities);
		},

		groups() {
			const groups = this.$store.getters["accessGroups/groups"];
			return this.filterRightAssociations(groups);
		},

		systems() {
			const rightSystems = this.rightSystems;

			return this.rightCommunities.reduce((systems, community) => {
				const leftSystems = this.filterRightAssociations(
					rightSystems[community.name]
				);
				systems[community.name] = leftSystems;

				return systems;
			}, {});
		},

		leftAssociationsByTypes() {
			const associations = {};
			associations["Communities"] = this.communities;
			associations["Groups"] = this.groups;

			Object.keys(this.systems).forEach((key) => {
				associations["Systems - " + key] = this.systems[key];
			});

			return associations;
		},

		rightAssociationsByTypes() {
			return this.associations.reduce((types, item) => {
				if (!["community", "association_group", "system"].includes(item.type))
					return types;
				const type = _.startCase(item.type);
				if (types[type]) types[type].push(item);
				else types[type] = [item];
				return types;
			}, {});
		},

		rightCommunities() {
			return this.rightAssociationsByTypes.Community || [];
		},
	},

	watch: {
		rightCommunities(communities) {
			communities.forEach(this.getSystems);
		},
	},

	created() {
		this.$store.dispatch("accessGroups/getGroups");
		this.rightCommunities.forEach(this.getSystems);
	},

	methods: {
		...mapActions("userEdit", ["changeUserAssociationRole"]),

		async changeAssociationRole(itemId, method) {
			itemId = await this.getAssociationsIds(itemId);
			await this.changeUserAssociationRole({
				userId: this.userId,
				method,
				itemId,
			});
		},

		filterRightAssociations(list) {
			return _.differenceBy(list, this.associations, "id");
		},

		async getSystems(community) {
			if (this.rightSystems[community.name]) return;

			const systems = await api.getSystems(community.identifier);

			this.rightSystems = {
				...this.rightSystems,
				[community.name]: systems,
			};
		},

		async getAssociationsIds(identifiers) {
			let systemIds = identifiers.filter(_.isString);
			if (!systemIds.length) return identifiers;

			systemIds = systemIds.map((id) => api.findAssociationByIdentifier(id));

			systemIds = await Promise.all(systemIds);

			const associations = identifiers
				.filter(_.isNumber)
				.concat(systemIds.map((association) => association.id));

			return associations;
		},
	},
};
</script>

<style lang="scss" scoped>
.selection-group {
	height: 30vh;
	margin-bottom: 6px;
}

@media (max-width: 730px) {
	.selection-group {
		height: 80vh;
	}
}
</style>
